import { useMemo } from "react";
import { useClientReservations } from "../../../hooks/useReservations";
import { compareDateOnly } from "../../shared/utils/dateUtils";
import type { Reservation } from "../types/Reservation";

export interface EstadiasPrevias {
  /** Cuántas estadías previas tuvo el cliente, sin contar la que se está mirando. */
  cantidad: number;
  /** Departamentos donde ya estuvo, de la estadía más reciente a la más vieja. */
  departamentos: string[];
  /** Número de orden de la estadía actual: 1 es la primera vez. */
  numeroDeEstadia: number;
  cargando: boolean;
}

const CANCELADA = /cancel/i;

/**
 * Responde "¿este cliente ya estuvo, y en qué departamento?" sin salir de la
 * pantalla actual. Reutiliza useClientReservations, que ya cachea por cliente,
 * así que abrir varias reservas del mismo cliente no repite la consulta.
 */
export const useEstadiasPrevias = (
  clienteId: number | null | undefined,
  reservaActualId: number | null | undefined,
  entradaActual?: string | null,
  /** Resumen ya calculado por el backend. Cuando llega, se usa tal cual y no se
   *  consulta nada: el listado lo resuelve en una sola query para toda la
   *  página, y es la fuente preferida para que no haya dos verdades. */
  resumenDelBackend?: Reservation["estadias_previas"]
): EstadiasPrevias => {
  const usarBackend = resumenDelBackend !== undefined;
  const { data, isPending } = useClientReservations(
    usarBackend ? null : clienteId ?? null
  );

  return useMemo(() => {
    if (resumenDelBackend) {
      return {
        cantidad: resumenDelBackend.cantidad,
        departamentos: resumenDelBackend.departamentos,
        numeroDeEstadia: resumenDelBackend.cantidad + 1,
        cargando: false,
      };
    }
    if (usarBackend) {
      return { cantidad: 0, departamentos: [], numeroDeEstadia: 1, cargando: false };
    }

    const todas: Reservation[] = Array.isArray(data)
      ? (data as Reservation[])
      : ((data as { reservas?: Reservation[] })?.reservas ?? []);

    const previas = todas
      .filter((r) => r.id !== reservaActualId)
      .filter((r) => !CANCELADA.test(String(r.estado ?? "")))
      .filter((r) => r.entrada)
      // Sólo lo anterior a esta estadía: una reserva futura del mismo cliente
      // no es un antecedente, y contarla haría decir "ya estuvo" sobre algo
      // que todavía no ocurrió.
      .filter((r) => !entradaActual || compareDateOnly(r.entrada, entradaActual) < 0)
      .sort((a, b) => compareDateOnly(b.entrada, a.entrada));

    const departamentos: string[] = [];
    for (const r of previas) {
      const depto = String(r.departamento ?? "").trim();
      if (depto && !departamentos.includes(depto)) departamentos.push(depto);
    }

    return {
      cantidad: previas.length,
      departamentos,
      numeroDeEstadia: previas.length + 1,
      cargando: Boolean(clienteId) && isPending,
    };
  }, [
    data,
    isPending,
    clienteId,
    reservaActualId,
    entradaActual,
    resumenDelBackend,
    usarBackend,
  ]);
};
